import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { makeStyles } from "@material-ui/core";
import ModalLayout from "@View/components/ModalLayout";
import ButtonSave from "@View/components/buttons/ButtonSave";
import DefaultInput from "@View/components/formInputs/DefaultInput";
import DefaultSelect from "@View/components/formInputs/DefaultSelect";
import { ClientRolesForSelect } from "@Utils/types";
import getAPI from "@API/index";

const useStyles = makeStyles(() => ({
  errorText: {
    fontSize: "12px",
    color: "rgba(164, 36, 28, 1)",
    marginBottom: "8px",
  },
}));

const types = [{ value: "Company" }, { value: "Entrepreneur" }];

const NewClientPage = ({ customerId, open, handleClose, onSaved }) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const groupsList = useSelector((state) => state.customer.groups);
  const groupsListValue = useMemo(
    () => groupsList.map((i) => ({ name: i.name, value: i.id })),
    [groupsList]
  );

  const initialForm = {
    externalId: "",
    type: "Company",
    name: "",
    email: "",
    customerId,
    role: null,
    customerGroupId: null,
  };

  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [globalError, setGlobalError] = useState("");

  useEffect(() => {
    if (open) {
      setForm({ ...initialForm });
      setGlobalError("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, customerId]);

  const setField = (field) => (value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (saving) return;

    const payload = {
      externalId: form.externalId.trim(),
      type: form.type,
      name: form.name.trim(),
      email: form.email.trim(),
      customerId: form.customerId,
      role: form.role,
      customerGroupId: form.customerGroupId,
    };

    setSaving(true);
    setGlobalError("");
    getAPI()
      .request({
        method: "post",
        url: "/api/Client",
        data: payload,
        successText: "snackbar.success",
        suppressError: false,
      })
      .then(() => {
        onSaved?.();
        handleClose();
      })      
      .finally(() => setSaving(false));
  };

  const footer = (
    <>
      {globalError && <div className={classes.errorText}>{globalError}</div>}
      <ButtonSave marginTop="0" disabled={saving}>
        buttons.Save
      </ButtonSave>
    </>
  );

  return (
    <ModalLayout
      open={open}
      onClose={handleClose}
      title="h1.New Client"
      onSubmit={handleSubmit}
      footer={footer}
    >
      <DefaultInput
        value={form.email}
        label="inputs.Email"
        onChange={setField("email")}
      />
      <DefaultInput
        value={form.externalId}
        label="inputs.External Id"
        onChange={setField("externalId")}
      />
      <DefaultInput
        value={form.name}
        label="inputs.CompanyName"
        onChange={setField("name")}
      />
      <DefaultSelect
        value={form.type}
        label="inputs.Type"
        dropdowns={types}
        onChange={setField("type")}
      />
      <DefaultSelect
        value={form.role}
        label="inputs.role"
        dropdowns={ClientRolesForSelect}
        onChange={setField("role")}
      />
      <DefaultSelect
        value={form.customerGroupId}
        label="inputs.group"
        dropdowns={groupsListValue}
        onChange={setField("customerGroupId")}
      />
    </ModalLayout>
  );
};

export default NewClientPage;
